import { useMemo, useSyncExternalStore } from 'react';
import { usGameKeyForBoardKey } from '@/lib/polymarketUs/gameMapping';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { useMarketStore } from '@/stores/marketStore';
import type { UsBrowserBoardQuote } from '@/stores/usBoardQuotes';

const subscribeAuthEpoch = (notify: () => void) => useAuthStore.subscribe(notify);
const subscribePageVisibility = (notify: () => void) => {
  document.addEventListener('visibilitychange', notify);
  return () => document.removeEventListener('visibilitychange', notify);
};
const readPageVisibility = () => document.visibilityState === 'visible';

/** #1207 — one game's public US moneyline quote for Game Detail, from the
 * quotes the shared feed already delivered (`quoteForGame`). No catalogue
 * poll, socket, REST or account read. Null for a key US does not list, while
 * the page is hidden, or when the store holds no single owned quote. */
export function useUsGameQuote(boardKey: string | null): UsBrowserBoardQuote | null {
  const version = useMarketStore((s) => s.version);
  const sessionEpoch = useSyncExternalStore(subscribeAuthEpoch, getAuthSessionEpoch);
  const pageVisible = useSyncExternalStore(subscribePageVisibility, readPageVisibility);
  const usKey = boardKey ? usGameKeyForBoardKey(boardKey) : null;
  return useMemo(() => {
    if (!pageVisible || !usKey) return null;
    return useMarketStore.getState().getUsBoardQuote(usKey);
    // The feed cache publishes through version; a login change also discards
    // the memo. Neither token is quote or account evidence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, usKey, pageVisible, sessionEpoch]);
}
